import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { BETA_FB } from "@/constants/testIds";
import {
  SECTIONS,
  visibleQuestions,
  requiresComment,
  isSectionComplete,
  isCommentMissing,
  collectMissingCommentIds,
  serialise,
} from "@/lib/betaFeedback/schema";
import { bfBundle, strongPromptKey } from "@/lib/betaFeedback/i18n";
import { buildBetaFeedbackFilename } from "@/lib/betaFeedback/filename";

// localStorage key for the draft — scoped to session so different
// interviews don't overwrite each other's drafts.
const draftKey = (sessionId) => `bf_draft_${sessionId}`;

function ChoiceRow({ question, value, onChange, choiceLabels, testIdPrefix, ariaLabel }) {
  return (
    <div
      className="flex flex-wrap gap-2"
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={`${testIdPrefix}-choices`}
    >
      {question.choices.map((c) => {
        const active = value === c;
        return (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(c)}
            data-testid={`${testIdPrefix}-choice-${c}`}
            className={[
              "px-4 py-2 rounded-full border text-sm transition-all",
              active
                ? "bg-[color:var(--brand)] border-[color:var(--brand)] text-white shadow-sm"
                : "bg-white border-[color:var(--border-stone)] text-[color:var(--text-primary)] hover:border-[color:var(--brand)]",
            ].join(" ")}
          >
            {choiceLabels[c] || c}
          </button>
        );
      })}
    </div>
  );
}

function QuestionBlock({ section, question, bundle, value, onChange, index, submitAttempted }) {
  const testIdPrefix = `bf-q-${question.id}`;
  const questionText = bundle.questions[question.id] || question.id;
  const isChoice = question.type === "choice";
  const isText = question.type === "text";

  const currentChoice = value?.choice || "";
  const currentComment = value?.comment ?? "";

  // "Comment required" state — driven by the schema:
  //   • choice question whose current pick is in `requiresComment[]`
  //   • text-only question flagged with `required: true`
  //     (currently only `rp_why_not`, spec 2026-02-16).
  const commentRequired =
    (isChoice && requiresComment(question, currentChoice))
    || (isText && Boolean(question.required));
  // Show the inline validation error only AFTER the user has tried
  // to submit at least once — never right after selecting an option
  // (spec 2026-02-16).
  const showError =
    submitAttempted && commentRequired && currentComment.trim().length === 0;

  const promptCopy = commentRequired
    ? bundle[strongPromptKey(question.id)] || bundle.promptStrong
    : bundle.promptHint;

  // Comment field styling:
  //  - required (any time): stronger red border + subtle red tint,
  //    so the affordance is more prominent than the old thin orange.
  //  - error (post-submit, still empty): amp up the ring so screen
  //    readers + sighted users notice, plus render the inline error
  //    message underneath.
  const textareaCls = [
    "min-h-[88px] resize-y transition-colors",
    commentRequired
      ? "border-2 border-red-500 bg-red-50 focus-visible:ring-red-400"
      : "",
    showError ? "ring-2 ring-red-400" : "",
  ].join(" ");

  return (
    <div
      className="rounded-2xl bg-white border border-[color:var(--border-stone)] p-6 space-y-4"
      data-testid={testIdPrefix}
      data-question-index={index}
      data-comment-required={commentRequired ? "yes" : "no"}
      data-comment-error={showError ? "yes" : "no"}
    >
      <div className="space-y-1">
        <div className="text-[color:var(--text-primary)] font-medium leading-snug">
          {questionText}
        </div>
        {isChoice && question.required && (
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">
            {bundle.requiredLabel}
          </div>
        )}
        {isText && question.required && (
          <div className="text-[10px] uppercase tracking-widest font-semibold text-red-700">
            {bundle.requiredLabel}
          </div>
        )}
        {isText && !question.required && (
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">
            {bundle.optionalLabel}
          </div>
        )}
      </div>

      {isChoice && (
        <ChoiceRow
          question={question}
          value={currentChoice}
          onChange={(c) => onChange({ choice: c, comment: currentComment })}
          choiceLabels={bundle.choiceLabels}
          testIdPrefix={testIdPrefix}
          ariaLabel={questionText}
        />
      )}

      <div>
        {isChoice && (
          <label
            className={[
              "block text-xs mb-1.5 font-medium",
              commentRequired
                ? "text-red-700"
                : "text-[color:var(--text-secondary)]",
            ].join(" ")}
            htmlFor={`${testIdPrefix}-comment`}
            data-testid={`${testIdPrefix}-comment-label`}
          >
            {commentRequired ? bundle.commentRequiredLabel : bundle.commentLabel}
          </label>
        )}
        <Textarea
          id={`${testIdPrefix}-comment`}
          data-testid={`${testIdPrefix}-comment`}
          placeholder={promptCopy}
          value={currentComment}
          onChange={(e) =>
            onChange({ choice: currentChoice, comment: e.target.value })
          }
          aria-required={commentRequired || undefined}
          aria-invalid={showError || undefined}
          className={textareaCls}
          maxLength={4000}
        />
        {commentRequired && (
          <p
            className="mt-1.5 text-xs text-red-700"
            data-testid={`${testIdPrefix}-comment-helper`}
          >
            {bundle.commentRequiredHelper}
          </p>
        )}
        {showError && (
          <p
            className="mt-1 text-xs text-red-700 font-medium"
            role="alert"
            data-testid={`${testIdPrefix}-comment-error`}
          >
            {bundle.commentRequiredError}
          </p>
        )}
      </div>
    </div>
  );
}

function SectionCard({ section, bundle, values, onChangeQuestion, sectionIndex, submitAttempted }) {
  const visible = visibleQuestions(section, values);
  const complete = isSectionComplete(section, values);
  return (
    <section
      data-testid={`bf-section-${section.key}`}
      data-section-complete={complete ? "yes" : "no"}
      className="space-y-4"
    >
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-semibold text-[color:var(--text-primary)]">
          {bundle.sectionTitles[section.key]}
        </h2>
        {complete && (
          <span
            className="inline-flex items-center gap-1 text-[color:var(--score-good,#2e7d32)] text-xs"
            data-testid={`bf-section-${section.key}-complete-badge`}
          >
            <CheckCircle size={16} weight="fill" />
          </span>
        )}
      </div>
      <div className="space-y-3">
        {visible.map((q, i) => (
          <QuestionBlock
            key={q.id}
            section={section}
            question={q}
            bundle={bundle}
            value={values[q.id]}
            onChange={(v) => onChangeQuestion(q.id, v)}
            index={i}
            submitAttempted={submitAttempted}
          />
        ))}
      </div>
    </section>
  );
}

async function downloadPdfBlob(feedbackId, displayName, submittedAt) {
  const res = await api.get(`/beta-feedback/${feedbackId}/pdf`, {
    responseType: "blob",
  });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = buildBetaFeedbackFilename(displayName, submittedAt);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function BetaFeedback() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const lang = user?.preferred_language || "en";
  const bundle = useMemo(() => bfBundle(lang), [lang]);

  const [values, setValues] = useState({});
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Once the user has tried to submit at least once, we start showing
  // inline validation errors for missing required comments. Reset to
  // `false` on mount (draft-restored) so the freshly-restored form
  // doesn't scream at the tester.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const draftRestoredToastFired = useRef(false);

  // Eligibility check on mount:
  //  1. Session must belong to the authenticated user AND be
  //     completed. `/beta-feedback/status/{sessionId}` doesn't
  //     leak session ownership, so we also ping the report endpoint
  //     to check completion. On any failure we bounce back to the
  //     Results page (or Home if we don't have a report yet).
  //  2. Prior submission → redirect to Results with a toast.
  useEffect(() => {
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    if (!user?.beta_access_approved) {
      toast.error(bundle.notEligibleToast);
      navigate(`/report/${sessionId}`, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [report, status] = await Promise.all([
          api.get(`/sessions/${sessionId}/report`).catch(() => null),
          api.get(`/beta-feedback/status/${sessionId}`).catch(() => null),
        ]);
        if (cancelled) return;
        if (!report || report.data?.status !== "completed") {
          toast.error(bundle.notEligibleToast);
          navigate(`/report/${sessionId}`, { replace: true });
          return;
        }
        if (status?.data?.submitted) {
          toast.error(bundle.alreadySubmittedToast);
          navigate(`/report/${sessionId}`, { replace: true });
          return;
        }
        // Restore any saved draft — keyed to this session id.
        try {
          const raw = window.localStorage.getItem(draftKey(sessionId));
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") {
              setValues(parsed);
              if (!draftRestoredToastFired.current) {
                draftRestoredToastFired.current = true;
                toast.success(bundle.draftRestored);
              }
            }
          }
        } catch {
          // Corrupt draft — ignore.
        }
        setReady(true);
      } catch {
        if (!cancelled) navigate(`/report/${sessionId}`, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, user?.beta_access_approved]);

  // Debounced draft persist — writes on every value change.
  useEffect(() => {
    if (!ready || !sessionId) return;
    try {
      window.localStorage.setItem(draftKey(sessionId), JSON.stringify(values));
    } catch {
      // localStorage may be blocked (private mode, quota exceeded) —
      // silently ignore. Submission still works.
    }
  }, [values, ready, sessionId]);

  function handleChangeQuestion(qid, next) {
    setValues((prev) => {
      const nextValues = { ...prev, [qid]: next };
      // Cascade: when a gate is switched, drop hidden gated followups
      // so they don't leak into the payload.
      for (const section of SECTIONS) {
        for (const q of section.questions) {
          if (!q.gatedBy) continue;
          const parent = nextValues[q.gatedBy.id];
          const visible = parent && q.gatedBy.showWhen.includes(parent.choice);
          if (!visible && nextValues[q.id]) {
            delete nextValues[q.id];
          }
        }
      }
      return nextValues;
    });
  }

  const sectionsComplete = useMemo(
    () => SECTIONS.filter((s) => isSectionComplete(s, values)).length,
    [values]
  );
  const totalSections = SECTIONS.length;
  const allComplete = sectionsComplete === totalSections;

  async function handleSubmit() {
    if (submitting) return;
    // Flip on inline validation from this point forward — required
    // comment errors are hidden pre-submit per the spec.
    setSubmitAttempted(true);
    if (!allComplete) {
      toast.error(bundle.incompleteToast);
      // Prefer scrolling to the first MISSING REQUIRED COMMENT (that
      // now displays its inline error) so the tester's eye lands on
      // the exact problem field. Fall back to the first incomplete
      // section if the gap is a missing choice instead.
      const missingComments = collectMissingCommentIds(values);
      if (missingComments.length > 0) {
        const el = document.querySelector(
          `[data-testid="bf-q-${missingComments[0]}"]`
        );
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          // Also focus the textarea so keyboard users can start typing.
          const ta = el.querySelector("textarea");
          if (ta) setTimeout(() => ta.focus({ preventScroll: true }), 350);
        }
      } else {
        const firstIncomplete = SECTIONS.find((s) => !isSectionComplete(s, values));
        if (firstIncomplete) {
          const el = document.querySelector(
            `[data-testid="bf-section-${firstIncomplete.key}"]`
          );
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        session_id: sessionId,
        language: lang,
        sections: serialise(values),
        final_comment: "",
      };
      const res = await api.post("/beta-feedback", payload);
      const feedbackId = res?.data?.feedback_id;
      const submittedAt = res?.data?.submitted_at;
      // Clear the draft on success — no need to keep it around.
      try {
        window.localStorage.removeItem(draftKey(sessionId));
      } catch {
        // ignore
      }
      // Auto-download the PDF — user can re-download from admin later
      // if this fails, but we surface a toast either way.
      if (feedbackId) {
        try {
          await downloadPdfBlob(
            feedbackId,
            user?.first_name || user?.email,
            submittedAt,
          );
        } catch {
          // PDF download failure shouldn't block the confirmation —
          // the submission itself succeeded.
        }
      }
      toast.success(bundle.submitSuccessToast);
      navigate(`/report/${sessionId}`, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(bundle.alreadySubmittedToast);
        navigate(`/report/${sessionId}`, { replace: true });
        return;
      }
      // Surface the actual server detail so beta testers (and
      // admins smoke-testing this) can see what's wrong instead of
      // just a generic error. Falls back to the localised message.
      const msg = typeof detail === "string" && detail
        ? detail
        : bundle.submitErrorToast;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!ready) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-[color:var(--text-secondary)]"
        data-testid="bf-loading"
      >
        …
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--surface-cream,#faf7f2)] pb-32" data-testid={BETA_FB.container}>
      <header className="border-b border-[color:var(--border-stone)] bg-white/70 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/brand/logo.png"
              alt="My English Coach Interview Simulator logo"
              className="h-[52px] w-[52px] object-contain"
            />
            <div>
              <div className="font-semibold tracking-tight" data-testid="bf-page-title">
                {bundle.pageTitle}
              </div>
              <div className="text-xs text-[color:var(--text-secondary)] max-w-md">
                {bundle.headerSubtitle}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            className="rounded-full"
            data-testid={BETA_FB.backBtn}
            onClick={() => navigate(`/report/${sessionId}`)}
          >
            {bundle.back}
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 pt-10 space-y-10">
        {SECTIONS.map((section, idx) => (
          <SectionCard
            key={section.key}
            section={section}
            bundle={bundle}
            values={values}
            onChangeQuestion={handleChangeQuestion}
            sectionIndex={idx}
            submitAttempted={submitAttempted}
          />
        ))}
      </main>

      {/* Sticky submit bar */}
      <div
        className="fixed inset-x-0 bottom-0 border-t border-[color:var(--border-stone)] bg-white/95 backdrop-blur-xl"
        data-testid={BETA_FB.stickyBar}
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div
            className="text-sm text-[color:var(--text-secondary)]"
            data-testid={BETA_FB.progressLabel}
          >
            {bundle.progress(sectionsComplete, totalSections)}
          </div>
          <Button
            className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white disabled:opacity-60"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid={BETA_FB.submitBtn}
          >
            {submitting ? bundle.submitting : bundle.submit}
          </Button>
        </div>
      </div>
    </div>
  );
}
